/* eslint-disable react/require-default-props */
import {
  FormControl,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { memo } from 'react';
import { NumericFormat } from 'react-number-format';

import {
  TypologypropertyType,
  TypologyRuleKeyword,
} from '../../../../../../../api/theHive/typology/enumerations';
import {
  menuItemDefaultStyle,
  textFieldDefaultStyle,
} from '../../../../../../../components/UI/Typography';
import { IconDeleteMS } from '../../../../../../../constants/icons';
import {
  selectTypologyPropertyType,
  selectTypologyRuleKeyword,
} from '../constants';
import { StyledInputLabel, StyledSelect } from '../styles';
import { Typology, TypologyRange } from '../types';

interface TypologyItemProps {
  typology: Typology;
  typologyIndex: number;
  onTypologyChange: <K extends keyof Typology>(
    typologyIndex: number,
    field: K,
    value: Typology[K]
  ) => void;
  onDeleteTypology?: (typologyIndex: number) => void;
  canDelete?: boolean;
}

const TypologyItem = memo(
  ({
    typology,
    typologyIndex,
    onTypologyChange,
    onDeleteTypology,
    canDelete,
  }: TypologyItemProps) => {
    const handleFieldChange = <K extends keyof Typology>(
      field: K,
      value: Typology[K]
    ): void => {
      onTypologyChange(typologyIndex, field, value);
    };

    const { range } = typology;
    const showArea =
      range === TypologyRuleKeyword.UP_TO ||
      range === TypologyRuleKeyword.OVER ||
      range === TypologyRuleKeyword.BETWEEN;

    return (
      <Grow
        in={typology.visible}
        timeout={300}
        unmountOnExit
        style={{ transformOrigin: '0 0 0' }}
      >
        <Grid item container spacing={2} alignItems="center" mt="2px">
          <Grid item>
            <FormControl sx={{ width: '260px' }} required>
              <StyledInputLabel id="property-type-label">tipo</StyledInputLabel>
              <StyledSelect
                labelId="property-type-label"
                label="tipo"
                value={typology.type ?? ''}
                onChange={(e) =>
                  handleFieldChange(
                    'type',
                    e.target.value as TypologypropertyType
                  )
                }
              >
                {selectTypologyPropertyType().map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={menuItemDefaultStyle}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>

          <Grid item xs={2.5}>
            <FormControl required sx={{ width: '100%' }}>
              <StyledInputLabel id="range-label">limite</StyledInputLabel>
              <StyledSelect
                labelId="range-label"
                label="limite"
                value={typology.range || ''}
                onChange={(e) => {
                  const value = e.target.value as TypologyRange;
                  handleFieldChange('range', value);
                  if (value === TypologyRuleKeyword.BETWEEN) {
                    handleFieldChange('area2', '');
                  }
                }}
              >
                {selectTypologyRuleKeyword().map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={menuItemDefaultStyle}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Grid>

          {showArea && (
            <Grid item xs={2}>
              <NumericFormat
                required
                customInput={TextField}
                label="(m²)"
                value={typology.area || ''}
                onChange={(e) => {
                  const newValue = e.target.value.replace(/\D/g, '');
                  handleFieldChange('area', newValue);
                }}
                thousandsGroupStyle="thousand"
                thousandSeparator=" "
                decimalScale={0}
                allowNegative={false}
                inputProps={{ maxLength: 10 }}
                sx={textFieldDefaultStyle}
              />
            </Grid>
          )}

          {typology.range === TypologyRuleKeyword.BETWEEN && (
            <>
              <Grid item>
                <span>e</span>
              </Grid>
              <Grid item xs={2}>
                <NumericFormat
                  required
                  customInput={TextField}
                  label="(m²)"
                  value={typology.area2 || ''}
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/\D/g, '');
                    handleFieldChange('area2', newValue);
                  }}
                  thousandsGroupStyle="thousand"
                  thousandSeparator=" "
                  decimalScale={0}
                  allowNegative={false}
                  inputProps={{ maxLength: 10 }}
                  sx={textFieldDefaultStyle}
                />
              </Grid>
            </>
          )}

          {canDelete && onDeleteTypology && (
            <Grid item>
              <IconButton
                onClick={() => onDeleteTypology(typologyIndex)}
                sx={(theme) => ({
                  color: '#707070',
                  '&:hover': {
                    color: theme.palette.secondary.main,
                  },
                })}
              >
                {IconDeleteMS}
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grow>
    );
  }
);

export default TypologyItem;
